import React, { useState, useEffect, useCallback } from 'react';
import { useLoadScript } from '@react-google-maps/api';
import Login from './components/Login';
import Logout from './components/Logout';
import Dashboard from './components/Dashboard';
import Future from './components/Dashboard/Future';
import Dispatch from './components/Dispatch/Dispatch';
import CompleteShift from './components/Dispatch/CompleteShift';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import BrandLoader from './components/BrandLoader';
import EmergencyAlarm from './components/EmergencyAlarm';
import { useWakeLock } from './hooks/useWakeLock';
import { useDynamicBranding } from './hooks/useDynamicBranding';
import { useVersionCheck } from './hooks/useVersionCheck';
import { Box, Alert, Toolbar } from '@mui/material';

// Define libraries as static constant to avoid re-creation each render
const GOOGLE_MAPS_LIBRARIES = ['places'];

function App() {
  const [socket, setSocket] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeComponent, setActiveComponent] = useState('login');
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('userSettings'));
  const [isNavigating, setIsNavigating] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [brandingReady, setBrandingReady] = useState(false);
  const [initialLoadStartTime] = useState(() => Date.now());
  
  // Version check for cache busting
  const { needsUpdate } = useVersionCheck();

  // Wake Lock to keep screen active
  const { isSupported: wakeLockSupported, isActive: wakeLockActive, requestWakeLock, error: wakeLockError } = useWakeLock();
  
  // Add state for shared data that was previously in localStorage
  const [userSettings, setUserSettings] = useState(() => {
    const stored = localStorage.getItem('userSettings');
    return stored ? JSON.parse(stored) : null;
  });
  
  const [fleetSettings, setFleetSettings] = useState(() => {
    const stored = localStorage.getItem('fleetSettings');
    return stored ? JSON.parse(stored) : null;
  });
  
  // Get fleet code for dynamic branding
  const fleetCode = fleetSettings?.unitId || localStorage.getItem('fleetCode') || null;
  
  // Apply dynamic branding based on fleet code
  useDynamicBranding(fleetCode, (ready) => {
    if (ready) {
      setBrandingReady(true);
    }
  });
  
  // Handle initial page load - wait for branding and React to be ready
  useEffect(() => {
    // Check if initial load conditions are met
    const checkInitialLoadComplete = () => {
      // Fleet code is detected (either from fleetSettings or localStorage)
      const hasFleetCode = fleetCode !== null || localStorage.getItem('fleetCode') !== null;
      
      // Branding is ready
      const brandingIsReady = brandingReady || !hasFleetCode; // If no fleet code, use default (ready)
      
      // React is mounted (component has rendered)
      const reactIsReady = true; // Always true once this effect runs
      
      // Minimum display time (400ms to prevent flicker)
      const minDisplayTime = 400;
      const elapsed = Date.now() - initialLoadStartTime;
      const minTimeElapsed = elapsed >= minDisplayTime;
      
      if (brandingIsReady && reactIsReady && minTimeElapsed) {
        // Add a small delay for smooth transition
        setTimeout(() => {
          setIsInitialLoading(false);
        }, 100);
      }
    };
    
    // Check immediately and also set up interval for polling
    checkInitialLoadComplete();
    
    // Poll every 50ms to check if conditions are met
    const interval = setInterval(checkInitialLoadComplete, 50);
    
    // Also check after a delay to ensure minimum display time
    const timeout = setTimeout(() => {
      checkInitialLoadComplete();
    }, 400);
    
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [fleetCode, brandingReady, initialLoadStartTime]);
  
  const [selectedVehicle, setSelectedVehicle] = useState(() => {
    const stored = localStorage.getItem('selectedVehicle');
    return stored ? JSON.parse(stored) : null;
  });
  
  // Note: selectedRoute and routeDetails are now managed through driverAuthData only
  // No longer using localStorage for these values - they come from auth response and socket updates
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [routeDetails, setRouteDetails] = useState(null);
  
  const googleKey =
    fleetSettings?.googleApiKey ||
    fleetSettings?.googleAPIKey ||
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const [googleApiKey, setGoogleApiKey] = useState(googleKey || null);

  useEffect(() => {
    const k =
      fleetSettings?.googleApiKey ||
      fleetSettings?.googleAPIKey ||
      process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
      null;
    if (k) setGoogleApiKey(k);
  }, [fleetSettings]);
  const [driverAuthData, setDriverAuthData] = useState(null);
  
  // Add state to track app mode (dashboard vs dispatch)
  const [appMode, setAppMode] = useState('dashboard'); // 'dashboard' or 'dispatch'
  
  // Add state to track current dispatch type for sidebar menu
  const [dispatchType, setDispatchType] = useState('live'); // 'live', 'itinerary', 'shuttlebus-looped', etc.
  
  // Request wake lock immediately when app loads to keep screen active at all times
  useEffect(() => {
    if (wakeLockSupported && !wakeLockActive) {
      requestWakeLock();
    }
  }, [wakeLockSupported, wakeLockActive, requestWakeLock]);

  // Log wake lock errors separately to avoid triggering re-requests
  useEffect(() => {
    if (wakeLockError) {
      console.warn('Wake Lock Error:', wakeLockError);
    }
  }, [wakeLockError]);
  
  // Load Google Maps script - always call hook but with conditional key
  // The hook needs a non-empty key to actually load
  const { isLoaded: googleMapsLoaded, loadError } = useLoadScript({
    googleMapsApiKey: googleApiKey || undefined, // Use undefined instead of empty string
    libraries: GOOGLE_MAPS_LIBRARIES,
    id: 'google-map-script', // Add ID to prevent multiple script loads
  });
  
  // Log Google Maps loading state for debugging (dev only)
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      console.log('App.js - Google Maps state:', {
        googleApiKey: googleApiKey ? 'present' : 'missing',
        googleApiKeyLength: googleApiKey?.length,
        googleMapsLoaded: googleMapsLoaded,
        loadError: loadError
      });
    }
  }, [googleApiKey, googleMapsLoaded, loadError]);

  const toggleSidebar = () => {

    setSidebarOpen(!sidebarOpen);
  };

  const handleRefreshClick = () => {
    window.location.href='/';
  };

  const handleLoginSuccess = useCallback((socketInstance, settings, nextComponent, authData) => {
    console.log('App: Starting login success handling with data:', { nextComponent, authData });
    
    // Show loader during login transition
    setIsNavigating(true);
    
    // Set socket first
    setSocket(socketInstance);
    console.log('App: Socket connected:', socketInstance);
    
    // Update fleet settings (this will trigger branding update via useDynamicBranding)
    setFleetSettings(settings);
    setGoogleApiKey(
      settings?.googleApiKey || process.env.REACT_APP_GOOGLE_MAPS_API_KEY || null
    );
    
    // Handle auth data (from new /Driver/Authorize endpoint)
    if (authData) {
      // Store dashboard data if available (only for dashboard mode)
      if (authData.dashboardData) {
        localStorage.setItem('dashboardData', JSON.stringify(authData.dashboardData));
      }
      
      // Process dispatch data - create merged auth data structure
      let mergedAuthData = { ...authData };
      
      if (authData.dispatchData) {
        // Merge the auth data with dispatch data to maintain full context
        mergedAuthData = {
          ...authData,
          ...authData.dispatchData, // Spread dispatch data properties to top level
          dispatchData: authData.dispatchData // But also keep the original structure
        };
        
        // CRITICAL: The spread above overwrites top-level driverObject (camelCase fields from REST)
        // with dispatchData.driverObject (PascalCase fields from formatCompleteDriverObject).
        // Preserve the top-level camelCase driverObject, since downstream code expects camelCase.
        // After server fix, both versions now have both casings, but this ensures the 
        // originally-mapped camelCase version takes priority for backward compatibility.
        if (authData.driverObject) {
          mergedAuthData.driverObject = authData.driverObject;
        }
        
        // If route data exists, set it in state (NOT localStorage)
        if (authData.dispatchData.routeObject && Object.keys(authData.dispatchData.routeObject).length > 0) {
          console.log('App: Setting initial route details from auth:', authData.dispatchData.routeObject);
          setRouteDetails(authData.dispatchData.routeObject);
        }
      }
      
      // Set the complete merged auth data
      console.log('App: Setting driver auth data:', mergedAuthData);
      setDriverAuthData(mergedAuthData);
    }
    
    // Sync userSettings from localStorage into React state.
    // comms.js stores userSettings to localStorage during auth, but without this
    // call the React state stays null until a page reload - causing downstream
    // components (DemandResponse, FlagTripDropoffModal) to receive null userSettings
    // and lose access to driverId.
    const storedUserSettings = localStorage.getItem('userSettings');
    if (storedUserSettings) {
      try {
        setUserSettings(JSON.parse(storedUserSettings));
      } catch (e) {
        console.error('App: Failed to parse userSettings from localStorage:', e);
      }
    }
    
    // Set authentication state
    setIsAuthenticated(true);
    
    // ── Dashboard landing override ───────────────────────────────────
    // The per-fleet config (admin.tranware.net) exposes `dashboard: true`
    // for fleets that should land on the home dashboard after login
    // (currently ACET). This flag is the authoritative, robust signal —
    // the config response carries no fleetCode/unitId field, so we cannot
    // rely on a fleetcode string match. Fleets without the flag keep the
    // existing defaultScreen/dispatch behavior unchanged (no regression).
    const resolvedFleetCode = (
      settings?.unitId ||
      settings?.fleetCode ||
      localStorage.getItem('fleetCode') ||
      ''
    ).toUpperCase();
    const dashboardEnabled =
      settings?.dashboard === true ||
      authData?.dashboard === true ||
      authData?.defaultScreen === 'dashboard' ||
      !!authData?.dashboardData;
    // The dashboard landing is intentionally limited to the ACET fleet. Other
    // fleets (e.g. DEVI) also ship `dashboard:true` in their server config, so
    // the flag alone is NOT a reliable ACET signal — without the fleet-code
    // gate every dashboard-flagged fleet would land on the home dashboard.
    // Gating on the ACET fleet code keeps all other fleets on their existing
    // dispatch landing behavior (no regression).
    const isAcetFleet = resolvedFleetCode === 'ACET';
    const forceDashboard = dashboardEnabled && isAcetFleet;
    console.log('App: dashboard routing decision:', {
      resolvedFleetCode,
      settingsDashboard: settings?.dashboard,
      dashboardEnabled,
      isAcetFleet,
      forceDashboard,
      nextComponent,
    });

    // Determine app mode based on ACET override, defaultScreen, or nextComponent
    const isDashboardMode =
      forceDashboard ||
      authData?.defaultScreen === 'dashboard' ||
      nextComponent === 'dashboard';
    setAppMode(isDashboardMode ? 'dashboard' : 'dispatch');

    // For ACET dashboard fleets, override the landing screen to the home dashboard.
    const targetComponent = forceDashboard ? 'dashboard' : nextComponent;

    // Use setTimeout to ensure all state updates are processed before navigation
    setTimeout(() => {
      console.log('App: Navigating to component:', targetComponent);
      if (targetComponent) {
        setActiveComponent(targetComponent);
      }
      // Hide loader after navigation completes
      setTimeout(() => {
        setIsNavigating(false);
      }, 200);
    }, 0);
  }, []); // Empty dependency array since we want a stable callback

  const handleLogout = () => {
    // Don't clear state immediately - let the Logout component handle the process
    setActiveComponent('logout');
  };

  const handleLogoutComplete = () => {
    // This will be called by the Logout component after it completes its cleanup
    setIsAuthenticated(false);
    setActiveComponent('login');
    setSocket(null);
    setUserSettings(null);
    setSelectedVehicle(null);
    setSelectedRoute(null);
    setRouteDetails(null);
    setDriverAuthData(null);
    
    // Clear localStorage - only remove items that should persist across sessions
    localStorage.removeItem('userSettings');
    // Note: selectedVehicle, fleetSettings, driverNumber, pin, fleetCode remain for convenience
    // dispatchData and routeDetails are not stored in localStorage anymore
  };

  // Handle post-shift-completion cleanup (bypasses Logout component since shift is already closed)
  const handleShiftCompleted = useCallback(() => {
    // CompleteShift already handled: shift completion, vehicle unassignment, odometer, inspection
    // Just clean up client state and navigate to login - preserve fleetCode and fleetSettings

    // Disconnect socket gracefully
    if (socket && typeof socket.disconnect === 'function') {
      try {
        socket.disconnect();
        console.log('Socket disconnected after shift completion');
      } catch (error) {
        console.error('Error disconnecting socket:', error);
      }
    }

    // Clear user-specific localStorage but PRESERVE fleetCode and fleetSettings
    localStorage.removeItem('userSettings');
    localStorage.removeItem('selectedRouteId');
    localStorage.removeItem('selectedRoute');
    localStorage.removeItem('routeDetails');
    localStorage.removeItem('driverNumber');
    localStorage.removeItem('pin');

    // Reset React state
    setIsAuthenticated(false);
    setActiveComponent('login');
    setIsNavigating(false);
    setSocket(null);
    setUserSettings(null);
    setSelectedVehicle(null);
    setSelectedRoute(null);
    setRouteDetails(null);
    setDriverAuthData(null);
  }, [socket]);

  const handleComponentChange = (componentName) => {
    // Show loader if component is actually changing
    if (componentName !== activeComponent) {
      setIsNavigating(true);
    }
    setActiveComponent(componentName);
    setSidebarOpen(false);
    
    // Hide loader after a short delay (component should be mounted by then)
    setTimeout(() => {
      setIsNavigating(false);
    }, 100);
  };

  // Handler for dispatch data updates (shift opening, route selection, etc.)
  const handleDispatchDataUpdate = useCallback((updateData) => {
    console.log('App: Dispatch data updated:', updateData);
    
    // Update driver auth data if provided
    if (updateData.driverAuthData) {
      console.log('App: Updating driver auth data:', updateData.driverAuthData);
      setDriverAuthData(updateData.driverAuthData);
    }
    
    // Update route details if provided (state only, no localStorage)
    if (updateData.routeDetails) {
      console.log('App: Updating route details:', updateData.routeDetails);
      setRouteDetails(updateData.routeDetails);
    }
    
    // Update selected vehicle if provided
    if (updateData.selectedVehicle) {
      console.log('App: Updating selected vehicle:', updateData.selectedVehicle);
      setSelectedVehicle(updateData.selectedVehicle);
      localStorage.setItem('selectedVehicle', JSON.stringify(updateData.selectedVehicle));
    }
  }, []);

  // Add this function to allow child components to request navigation
  const navigateToComponent = (componentName) => {
    // Show loader if component is actually changing
    if (componentName !== activeComponent) {
      setIsNavigating(true);
    }
    
    if (componentName === 'login') {
      handleLogout();
    } else if (componentName === 'logout') {
      handleLogout();
    } else if (componentName === 'dashboard') {
      setAppMode('dashboard');
      setActiveComponent('dashboard');
    } else if (componentName === 'Dashboard/Future' || componentName === 'future') { 
      setAppMode('dashboard');
      setActiveComponent('dashboard/future');
    } else if (componentName === 'Dispatch/CompleteShift' || componentName === 'completeShift') { 
      setAppMode('dispatch');
      setActiveComponent('dispatch/completeshift');
    } else {
      // All other components go through dispatch mode
      // The Dispatch component will handle internal routing
      setAppMode('dispatch');
      setActiveComponent('dispatch');
    }
    
    // Hide loader after component change
    setTimeout(() => {
      setIsNavigating(false);
    }, 100);
  };

  // Update shared data handlers
  const updateVehicle = (vehicle) => {
    setSelectedVehicle(vehicle);
    if (vehicle) {
      localStorage.setItem('selectedVehicle', JSON.stringify(vehicle));
    } else {
      localStorage.removeItem('selectedVehicle');
    }
  };

  const updateRoute = (route, details) => {
    setSelectedRoute(route);
    setRouteDetails(details);
    // Route data is now managed only in React state, not localStorage
    // This data comes from auth response initially, then from socket updates
  };

  // Show loading while Google Maps is loading (do not require userSettings — otherwise LiveDispatch
  // can mount GoogleMap before the script is ready and Maps v3 throws minified "pH" / undefined errors).
  if (googleApiKey && !googleMapsLoaded && !loadError) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column' 
      }}>
        <div style={{ 
          width: '40px', 
          height: '40px', 
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #3498db',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          marginBottom: '20px'
        }}></div>
        <p>Loading Google Maps...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Google Maps failed to load (e.g. no network, invalid key, quota exceeded).
  // Render the app without maps rather than crashing — components guard on googleMapsLoaded.
  if (loadError) {
    console.error('Google Maps failed to load:', loadError);
    // Do NOT return here — let the app continue with googleMapsLoaded=false so map
    // components render their "maps unavailable" fallback instead of crashing.
  }

  // Show initial load loader (full screen, highest priority) - must be checked before any other rendering
  if (isInitialLoading) {
    return <BrandLoader isLoading={true} fleetCode={fleetCode} minDisplayTime={400} />;
  }

  // Component map for rendering - pass shared data as props
  const componentMap = {
    'login': <Login 
      onLoginSuccess={handleLoginSuccess}
      fleetSettings={fleetSettings}
      selectedVehicle={selectedVehicle}
      onVehicleChange={updateVehicle}
      onRouteChange={updateRoute}
    />,
    'logout': <Logout 
      onLogout={handleLogoutComplete}
      userSettings={userSettings}
      selectedVehicle={selectedVehicle}
      driverAuthData={driverAuthData}
      selectedRoute={selectedRoute}
      fleetSettings={fleetSettings}
      routeDetails={routeDetails}
      socket={socket}
    />,
    'dashboard': <Dashboard 
      navigateTo={navigateToComponent}
      userSettings={userSettings}
      fleetSettings={fleetSettings}
    />,
    'dashboard/future': <Future 
      navigateTo={navigateToComponent}
      userSettings={userSettings}
      fleetSettings={fleetSettings}
    />,
    'dispatch': <Dispatch 
      navigateTo={navigateToComponent}
      userSettings={userSettings}
      fleetSettings={fleetSettings}
      socket={socket}
      routeDetails={routeDetails}
      driverAuthData={driverAuthData}
      selectedVehicle={selectedVehicle}
      onDispatchDataUpdate={handleDispatchDataUpdate}
      googleMapsLoaded={googleMapsLoaded}
      onDispatchTypeChange={setDispatchType}
    />,
    'dispatch/completeshift': <CompleteShift 
      navigateTo={navigateToComponent}
      userSettings={userSettings}
      fleetSettings={fleetSettings}
      socket={socket}
      routeDetails={driverAuthData?.routeObject || driverAuthData?.dispatchData?.routeObject || routeDetails}
      driverAuthData={driverAuthData}
      vehicleDetails={selectedVehicle}
      selectedVehicle={selectedVehicle}
      googleMapsLoaded={googleMapsLoaded || false}
      inspectionData={driverAuthData?.inspectionObject || driverAuthData?.inspectionData}
      onDispatchDataUpdate={handleDispatchDataUpdate}
      onShiftComplete={(completionData) => {
        // Shift is already completed server-side - bypass Logout component, go directly to login
        // This preserves fleetCode and fleetSettings in localStorage
        handleShiftCompleted();
      }}
    />
  };

  // Render login if not authenticated (except when logging out)
  if ((!isAuthenticated || activeComponent === 'login') && activeComponent !== 'logout') {
    return (
      <>
        {needsUpdate && (
          <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }}>
            <Alert severity="info">
              New version available. Updating in 3 seconds...
            </Alert>
          </Box>
        )}
        {componentMap['login']}
      </>
    );
  }

  // Handle logout component specially - it can render even when transitioning to unauthenticated
  if (activeComponent === 'logout') {
    return (
      <>
        {needsUpdate && (
          <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }}>
            <Alert severity="info">
              New version available. Updating in 3 seconds...
            </Alert>
          </Box>
        )}
        {componentMap['logout']}
      </>
    );
  }

  // Render authenticated app with conditional Header/Sidebar based on mode
  const isDashboardMode = appMode === 'dashboard' || activeComponent === 'dashboard' || activeComponent === 'dashboard/future';
  const isDispatchMode = appMode === 'dispatch' || activeComponent === 'dispatch' || activeComponent === 'dispatch/completeshift';
  
  // Get fleet code for loader branding
  const loaderFleetCode = fleetSettings?.unitId || localStorage.getItem('fleetCode') || null;
  
  if (isDashboardMode) {
    // Dashboard mode: Show Header + Sidebar + Component
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
        <BrandLoader isLoading={isNavigating} fleetCode={loaderFleetCode} />
        <Header 
          onMenuClick={toggleSidebar} 
          onRefreshClick={handleRefreshClick} 
          showMenuButton={true}
        />
        <Toolbar />
        <Sidebar 
          open={sidebarOpen} 
          onClose={toggleSidebar} 
          activeComponent={activeComponent}
          onComponentChange={handleComponentChange}
          userSettings={userSettings}
          fleetSettings={fleetSettings}
          navigateToComponent={navigateToComponent}
          dispatchType={dispatchType}
          socket={socket}
          selectedVehicle={selectedVehicle}
          routeDetails={routeDetails}
        />
        <Box sx={{ flexGrow: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}>
          {componentMap[activeComponent] || componentMap['dashboard']}
        </Box>
        <EmergencyAlarm showFloatingButton={false} />
      </Box>
    );
  } else if (isDispatchMode) {
    // Dispatch mode: Show Header + Sidebar + Component
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
        <BrandLoader isLoading={isNavigating} fleetCode={loaderFleetCode} />
        <Header 
          onMenuClick={toggleSidebar} 
          onRefreshClick={handleRefreshClick} 
          showMenuButton={true}
        />
        <Toolbar />
        <Sidebar 
          open={sidebarOpen} 
          onClose={toggleSidebar} 
          activeComponent={activeComponent}
          onComponentChange={handleComponentChange}
          userSettings={userSettings}
          fleetSettings={fleetSettings}
          navigateToComponent={navigateToComponent}
          dispatchType={dispatchType}
          socket={socket}
          selectedVehicle={selectedVehicle}
          routeDetails={routeDetails}
        />
        <Box sx={{ flexGrow: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}>
          {componentMap[activeComponent] || componentMap['dispatch']}
        </Box>
        <EmergencyAlarm showFloatingButton={false} />
      </Box>
    );
  } else {
    // Fallback: Show only the component (for logout, etc.)
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
        <BrandLoader isLoading={isNavigating} fleetCode={loaderFleetCode} />
        <Box sx={{ flexGrow: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}>
          {componentMap[activeComponent] || componentMap['dashboard']}
        </Box>
      </Box>
    );
  }
}

export default App;